/**
 * DashboardLayout — Dogfood shell built on @fragments-sdk/ui AppShell.
 * OrgProvider is mounted higher up (`_authed.tsx` / `demo.tsx`) so pages
 * can compute their aside node from org/project context before passing it.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight,
  CaretUp,
  Crown,
  List,
  MagnifyingGlass,
  SignOut,
  UserCircle,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useClerk, useUser } from '@clerk/tanstack-react-start';
import {
  AppShell,
  Sidebar,
  type AppShellLayout,
  type AppShellSlotVariant,
} from '@fragments-sdk/ui';
import {
  CLOUD_DASHBOARD_NAV_MAIN,
  CLOUD_DASHBOARD_NAV_TOOLS,
  type CloudDashboardNavItem,
} from './dashboard-nav-items';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  buildProjectSearch,
  buildSettingsPath,
  buildWorkspacePath,
  type WorkspaceSectionKey,
} from '@/lib/routes';
import { getPlanHighlights } from '../../../convex/billing/plans';
import {
  DashboardLayoutFrameContext,
  useIsomorphicLayoutEffect,
} from './DashboardLayoutFrameContext';
import { SidebarProjectHeader } from './DashboardSidebarProjectHeader';
import { SearchDialog } from './DashboardDialogs';
import s from './DashboardLayout.module.scss';

// ── Nav Data ────────────────────────────────────────────────────

const DASHBOARD_APPSHELL_PROPS = {
  layout: 'floating',
} satisfies { layout: AppShellLayout };

const DASHBOARD_APPSHELL_FLOATING_PROPS = {
  variant: 'floating',
} satisfies { variant: AppShellSlotVariant };

const NAV_LABEL_TO_SECTION = {
  Dashboard: 'dashboard',
  'Pull Requests': 'pullRequests',
  Backlog: 'findings',
  Governance: 'governance',
  'Developer Setup': 'mcp',
  Settings: 'settings',
} satisfies Record<string, WorkspaceSectionKey>;

function buildNavHref(label: string, orgSlug: string | null): string | null {
  if (!orgSlug) return null;
  const section =
    NAV_LABEL_TO_SECTION[label as keyof typeof NAV_LABEL_TO_SECTION];
  return section ? buildWorkspacePath(orgSlug, section) : null;
}

// ── Props ────────────────────────────────────────────────────────

export interface DashboardLayoutProps {
  activeNav: string;
  children: React.ReactNode;
  /** Remove AppShell.Main padding — for pages that manage their own spacing. */
  flush?: boolean;
  /** Optional replacement for the default first-layer dashboard navigation. */
  sidebarNav?: React.ReactNode;
  /** Optional right-hand filter/context panel (rendered into AppShell.Aside). */
  aside?: React.ReactNode;
  /** Optional width for AppShell.Aside. */
  asideWidth?: string;
}

// ── Nav Items ───────────────────────────────────────────────────

interface NavItemProps {
  item: CloudDashboardNavItem;
  activeNav: string;
  href: string | null;
  search?: Record<string, string>;
  isLoading: boolean;
  onItemClick?: (
    item: CloudDashboardNavItem,
    event: React.MouseEvent<HTMLElement>,
  ) => void;
}

function NavItem({
  item,
  activeNav,
  href,
  search,
  isLoading,
  onItemClick,
}: NavItemProps) {
  const Icon = item.icon;
  const isActive = activeNav === item.label;
  const icon = <Icon size={16} weight="regular" />;

  if (!href || isLoading) {
    return (
      <Sidebar.Item
        icon={icon}
        active={isActive}
        disabled
        data-tour={item.dataTour}
      >
        {item.label}
      </Sidebar.Item>
    );
  }

  return (
    <Sidebar.Item
      icon={icon}
      active={isActive}
      data-tour={item.dataTour}
      asChild
      onClick={(event) => onItemClick?.(item, event)}
    >
      <Link to={href as any} search={search as any} preload="intent">
        {item.label}
      </Link>
    </Sidebar.Item>
  );
}

export function DashboardNav({
  activeNav,
  onItemClick,
}: {
  activeNav: string;
  onItemClick?: (
    item: CloudDashboardNavItem,
    event: React.MouseEvent<HTMLElement>,
  ) => void;
}) {
  const { org, isLoading } = useOrgContext();
  const { resolvedSlug } = useProjectContext();
  const orgSlug = org?.slug ?? null;
  const search = buildProjectSearch(resolvedSlug);

  const renderItems = (items: CloudDashboardNavItem[]) =>
    items.map((item) => (
      <NavItem
        key={item.label}
        item={item}
        activeNav={activeNav}
        href={buildNavHref(item.label, orgSlug)}
        search={search}
        isLoading={isLoading}
        onItemClick={onItemClick}
      />
    ));

  return (
    <div data-tour="sidebar-nav">
      <Sidebar.Section>{renderItems(CLOUD_DASHBOARD_NAV_MAIN)}</Sidebar.Section>
      <Sidebar.Section label="Setup" collapsible defaultOpen>
        {renderItems(CLOUD_DASHBOARD_NAV_TOOLS)}
      </Sidebar.Section>
    </div>
  );
}

// ── Sidebar Footer ──────────────────────────────────────────────


function SidebarUpgradeCard({ orgSlug }: { orgSlug: string | null }) {
  const inner = (
    <>
      <span className={s.upgradeCardHeader}>
        <span className={s.upgradeCardIcon} aria-hidden>
          <Crown size={11} weight="fill" />
        </span>
        <span className={s.upgradeCardEyebrow}>Free plan</span>
      </span>
      <span className={s.upgradeCardTitle}>Upgrade to Pro</span>
      <span className={s.upgradeCardText}>{getPlanHighlights('pro')[3]}</span>
      <span className={s.upgradeCardCta}>
        View plans
        <ArrowRight size={10} weight="bold" />
      </span>
    </>
  );

  if (!orgSlug) {
    return <div className={s.upgradeCard}>{inner}</div>;
  }

  return (
    <Link
      to={buildSettingsPath(orgSlug) as any}
      search={{ tab: 'billing' } as any}
      className={s.upgradeCard}
    >
      {inner}
    </Link>
  );
}

function getInitials(name?: string | null, email?: string | null) {
  const source = name?.trim() || email?.trim() || 'User';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return source.slice(0, 1).toUpperCase();
}

function SidebarAccountMenu() {
  const clerk = useClerk();
  const { user } = useUser();
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const displayName =
    user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? 'Account';
  const email = user?.primaryEmailAddress?.emailAddress ?? null;
  const initials = getInitials(user?.fullName, email);

  React.useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const avatar = (
    <span className={s.accountAvatar}>
      {user?.imageUrl ? (
        <img src={user.imageUrl} alt="" />
      ) : (
        <span>{initials}</span>
      )}
    </span>
  );

  return (
    <div className={s.accountMenuWrap} ref={menuRef}>
      {open ? (
        <div className={s.accountMenu} role="menu">
          <div className={s.accountMenuHeader}>
            {avatar}
            <span className={s.accountMenuHeaderCopy}>
              <span className={s.accountName}>{displayName}</span>
              {email ? <span className={s.accountEmail}>{email}</span> : null}
            </span>
          </div>
          <div className={s.accountMenuDivider} />
          <button
            type="button"
            className={s.accountMenuItem}
            role="menuitem"
            onClick={() => {
              setOpen(false);
              clerk.openUserProfile();
            }}
          >
            <UserCircle size={14} />
            Account settings
          </button>
          <button
            type="button"
            className={s.accountMenuItem}
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void clerk.signOut({ redirectUrl: '/sign-in' });
            }}
          >
            <SignOut size={14} />
            Log out
          </button>
        </div>
      ) : null}
      <button
        type="button"
        className={s.accountButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {avatar}
        <span className={s.accountCopy}>
          <span className={s.accountName}>{displayName}</span>
          {email ? <span className={s.accountEmail}>{email}</span> : null}
        </span>
        <CaretUp
          size={11}
          weight="bold"
          className={s.accountChevron}
          data-open={open || undefined}
        />
      </button>
    </div>
  );
}

function useNarrowLayout() {
  const [isNarrow, setIsNarrow] = React.useState(false);

  React.useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)');
    setIsNarrow(query.matches);

    function handleChange(event: MediaQueryListEvent) {
      setIsNarrow(event.matches);
    }

    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return isNarrow;
}

// ── Sidebar Header Icon Buttons ─────────────────────────────────

function HeadIconButton({
  label,
  onClick,
  dataTour,
  children,
}: {
  label: string;
  onClick?: () => void;
  dataTour?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={s.headIconBtn}
      onClick={onClick}
      aria-label={label}
      title={label}
      data-tour={dataTour}
    >
      {children}
    </button>
  );
}

// ── Shell ───────────────────────────────────────────────────────

export function DashboardLayoutShell({
  activeNav,
  children,
  flush,
  sidebarNav,
  aside,
  asideWidth,
}: DashboardLayoutProps) {
  const [searchOpen, setSearchOpen] = React.useState(false);
  const { org } = useOrgContext();
  const orgSlug = org?.slug ?? null;
  const showUpgradeCard = org?.plan === 'free';
  const isNarrow = useNarrowLayout();
  const [mobileAsideOpen, setMobileAsideOpen] = React.useState(false);
  const showMobileAside = Boolean(aside) && isNarrow;

  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  React.useEffect(() => {
    if (!isNarrow) setMobileAsideOpen(false);
  }, [isNarrow]);

  return (
    <>
      <div className={s.shellHost}>
        <AppShell className={s.dashboardShell} {...DASHBOARD_APPSHELL_PROPS}>
          <Sidebar.Trigger className={s.mobileMenuBtn}>
            <List size={18} />
          </Sidebar.Trigger>
          <AppShell.Sidebar
            width="240px"
            collapsible="offcanvas"
            {...DASHBOARD_APPSHELL_FLOATING_PROPS}
          >
            <Sidebar.Header>
              <div className={s.sidebarHead}>
                <div className={s.sidebarHeadStart}>
                  <SidebarProjectHeader />
                </div>
                <div className={s.sidebarHeadActions}>
                  <HeadIconButton
                    label="Search (⌘K)"
                    dataTour="search-box"
                    onClick={() => setSearchOpen(true)}
                  >
                    <MagnifyingGlass size={16} />
                  </HeadIconButton>
                </div>
              </div>
            </Sidebar.Header>
            <Sidebar.Nav>
              {sidebarNav ?? <DashboardNav activeNav={activeNav} />}
            </Sidebar.Nav>
            <Sidebar.Footer>
              {showUpgradeCard && <SidebarUpgradeCard orgSlug={orgSlug} />}
              <SidebarAccountMenu />
            </Sidebar.Footer>
          </AppShell.Sidebar>

          <AppShell.Main
            padding={flush ? 'none' : 'md'}
            {...DASHBOARD_APPSHELL_FLOATING_PROPS}
          >
            {children}
          </AppShell.Main>

          {aside && !showMobileAside ? (
            <AppShell.Aside
              width={asideWidth}
              {...DASHBOARD_APPSHELL_FLOATING_PROPS}
            >
              {aside}
            </AppShell.Aside>
          ) : null}
        </AppShell>
      </div>

      {showMobileAside ? (
        <>
          <button
            type="button"
            className={s.mobileAsideToggle}
            aria-expanded={mobileAsideOpen}
            aria-controls="dashboard-mobile-aside"
            onClick={() => setMobileAsideOpen(true)}
          >
            Filters
          </button>
          <div
            className={s.mobileAsideLayer}
            data-open={mobileAsideOpen || undefined}
            aria-hidden={!mobileAsideOpen}
          >
            <button
              type="button"
              className={s.mobileAsideScrim}
              aria-label="Close filters"
              onClick={() => setMobileAsideOpen(false)}
            />
            <div
              id="dashboard-mobile-aside"
              className={s.mobileAsidePanel}
              role="dialog"
              aria-label="Filters"
            >
              {aside}
            </div>
          </div>
        </>
      ) : null}

      {searchOpen && <SearchDialog onClose={() => setSearchOpen(false)} />}
    </>
  );
}

// ── Component ────────────────────────────────────────────────────

export function DashboardLayout({
  activeNav,
  children,
  flush,
  sidebarNav,
  aside,
  asideWidth,
}: DashboardLayoutProps) {
  const frame = React.useContext(DashboardLayoutFrameContext);
  const hasAside = Boolean(aside);
  const hasSidebarNav = Boolean(sidebarNav);

  useIsomorphicLayoutEffect(() => {
    if (!frame) return;
    frame.setLayoutConfig({
      activeNav,
      flush: Boolean(flush),
      hasAside,
      asideWidth,
      hasSidebarNav,
    });
  }, [activeNav, asideWidth, flush, frame, hasAside, hasSidebarNav]);

  if (frame) {
    return (
      <>
        {children}
        {aside && frame.asideHost ? createPortal(aside, frame.asideHost) : null}
        {sidebarNav && frame.sidebarNavHost
          ? createPortal(sidebarNav, frame.sidebarNavHost)
          : null}
      </>
    );
  }

  return (
    <DashboardLayoutShell
      activeNav={activeNav}
      flush={flush}
      sidebarNav={sidebarNav}
      aside={aside}
      asideWidth={asideWidth}
    >
      {children}
    </DashboardLayoutShell>
  );
}
